import React from 'react';
import { useTranslation } from 'react-i18next';
import { Heart, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

const InvestmentSection = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  // Libellés neutres (sans référence à la région)
  const requestLabel =
    lang === 'ar'
      ? 'تفاصيل الاستثمار بعد  بعد الجلسة التعريفية'
      : lang === 'de'
      ? 'Individuelles Angebot nach Vorgespräch'
      : 'Proposition tarifaire après entretien';

  const requestNote =
    lang === 'ar'
      ? 'يُرسَل لكِ عرض واضح بعد مكالمة تعريفية قصيرة (٣٠ دقيقة)، وفق الإطار الذي تختارينه (شخصي/أعمال) والصيغة المرغوبة (جلسات، مسار قصير، مكثف).'
      : lang === 'de'
      ? 'Du erhältst ein klares Angebot nach einem kurzen Vorgespräch (30 Min.) – passend zu deinem Rahmen (Personal/Business) und Format (Einzelsitzungen, Kurzprozess, Intensiv).'
      : 'Une proposition claire vous est adressée après un court entretien (30 min), selon le cadre choisi (personnel/business) et le format souhaité (séances, court processus, intensif).';

  return (
    <section
      id="investissement"
      className="relative py-16 overflow-hidden min-h-[620px] md:min-h-[720px]"
    >
      {/* Background + overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1716848754354-b883bd1a066e"
          alt={t('investment.imageAlt')}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-charcoal/85 via-rich-chocolate/70 to-charcoal/80" />
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center space-x-2 mb-4">
            <Sparkles className="h-5 w-5 text-warm-sage animate-pulse" />
            <Sparkles className="h-4 w-4 text-soft-taupe animate-pulse" style={{ animationDelay: '0.3s' }} />
            <Sparkles className="h-5 w-5 text-warm-sage animate-pulse" style={{ animationDelay: '0.6s' }} />
          </div>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-light text-cream-white mb-3 leading-tight">
            {t('investment.title')}
          </h2>
          <p className="text-sm md:text-base font-light text-soft-taupe italic">
            {t('investment.tagline')}
          </p>
        </div>

        {/* Narrative */}
        <div className="max-w-4xl mx-auto space-y-8 mb-12">
          <p className="text-lg md:text-xl font-serif font-medium text-cream-white leading-tight text-center">
            {t('investment.p1')}
          </p>
          <p className="text-base md:text-lg font-light text-cream-white leading-relaxed text-center">
            {t('investment.p2')}
          </p>
          <p className="text-base md:text-lg font-light text-cream-white leading-relaxed text-center">
            {t('investment.p3')}
          </p>
          <p className="text-lg md:text-xl font-serif font-medium text-cream-white leading-tight text-center">
            {t('investment.p4_line1')}<br />
            <span className="text-warm-sage">{t('investment.p4_line2')}</span>
          </p>
          <p className="text-base md:text-lg font-serif font-light text-cream-white/100 leading-relaxed italic text-center">
            {t('investment.p5')}
          </p>
        </div>

        {/* Reveal */}
        <div className="text-center mb-8">
          <div className="flex justify-center space-x-2 mb-4">
            <Sparkles className="h-5 w-5 text-warm-sage animate-pulse" />
            <Sparkles className="h-4 w-4 text-soft-taupe animate-pulse" style={{ animationDelay: '0.3s' }} />
            <Sparkles className="h-5 w-5 text-warm-sage animate-pulse" style={{ animationDelay: '0.6s' }} />
          </div>

          <p className="text-sm md:text-base font-light text-soft-taupe leading-relaxed text-center">
            {t('investment.reveal_label')}
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-light text-cream-white mb-3 leading-tight">
            {t('investment.reveal_title')}
          </h2>
          <p className="text-lg md:text-xl font-serif font-medium text-cream-white leading-tight text-center">
            {t('investment.reveal_p1')}<br />
            <span className="text-warm-sage">{t('investment.reveal_p2')}</span>
          </p>
          <p className="text-base md:text-lg font-serif font-light text-cream-white/100 leading-relaxed italic text-center">
            {t('investment.reveal_p3')}
          </p>
          <p className="text-sm md:text-base font-light text-soft-taupe italic">
            {t('investment.reveal_note')}
          </p>
        </div>

        {/* Badge « proposition après entretien » (remplace la carte prix) */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-warm-sage/15 backdrop-blur-sm px-4 py-2 rounded-full border border-warm-sage/30">
            <Sparkles className="h-4 w-4 text-warm-sage" />
            <span className="text-sm font-medium text-cream-white/90">
              {requestLabel}
            </span>
          </div>

          <p className="text-xs md:text-sm font-light text-cream-white/80 italic mt-4 max-w-2xl mx-auto">
            {requestNote}
          </p>
        </div>

        {/* CTA */}
        <div className="text-center pt-6">
          <Button
            size="lg"
            onClick={() =>
              document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })
            }
            className="group relative bg-gradient-to-r from-soft-taupe via-warm-sage to-soft-taupe hover:from-warm-sage hover:to-soft-taupe text-cream-white font-medium px-10 py-4 rounded-xl transition-all duration-500 shadow-2xl hover:shadow-3xl hover:scale-105"
          >
            <span className="relative z-10 flex items-center">
              <Heart className="mr-2 h-5 w-5 group-hover:animate-pulse" />
              {t('investment.cta')}
              <Sparkles className="ml-2 h-5 w-5 group-hover:rotate-180 transition-transform duration-700" />
            </span>
          </Button>

          {t('investment.cta_note') && (
            <p className="text-xs md:text-sm text-cream-white/80 font-light italic mt-4">
              {t('investment.cta_note')}
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

export default InvestmentSection;
